import { ArrowRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { formatDateTime } from '@/lib/date';
import type { EmployeeAuditEntry } from '@/types/employee-audit';
import {
  actorInitials,
  describeActor,
  describeEntry,
} from '@/features/people/lib/employee-history';

const SOURCE_BADGES: Partial<Record<EmployeeAuditEntry['source'], string>> = {
  peopleforce: 'PeopleForce',
  preboarding: 'Pre-boarding',
  recruitment: 'Recruitment',
  system: 'System',
};

/**
 * One change-history entry, as the History tab and the Job tab's preview show
 * it [HW-795]. Renders an `<li>`, so it goes inside a `<ul>`.
 */
export function HistoryRow({ entry }: { entry: EmployeeAuditEntry }) {
  const change = describeEntry(entry);
  const who = describeActor(entry);
  const badge = entry.actor ? SOURCE_BADGES[entry.source] : undefined;
  return (
    <li className="flex items-start gap-3 py-3">
      <Avatar className="h-8 w-8 shrink-0 rounded-full" aria-hidden="true">
        {entry.actor?.avatarUrl && <AvatarImage src={entry.actor.avatarUrl} alt="" />}
        <AvatarFallback className="rounded-full text-xs">{actorInitials(entry)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          <span className="font-medium">{change.title}</span>
          {change.from !== undefined && (
            <span className="ml-2 inline-flex flex-wrap items-center gap-1.5 text-muted-foreground">
              <span className="sr-only">from </span>
              <del className="wrap-anywhere line-through">{change.from}</del>
              <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="sr-only">to </span>
              <ins className="wrap-anywhere text-foreground no-underline">{change.to}</ins>
            </span>
          )}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{who}</span>
          <span>·</span>
          <time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt)}</time>
          {badge && (
            <Badge variant="outline" className="h-5 px-1.5 text-[11px]">
              {badge}
            </Badge>
          )}
        </p>
      </div>
    </li>
  );
}
